import React from "/vendor/react";

import { AppLayout, BoundFilterList } from "/lib/component/partial";
import {
  EntitiesDashView,
  useEntitiesDashViewQueryVariables,
} from "/lib/component/view/EntitiesView";
import {
  EventsDashView,
  EventsList,
  useEventsDashViewQueryVariables,
} from "/lib/component/view/EventsView";
import { Grid } from "@material-ui/core";
import gql from "/vendor/graphql-tag";
import MapWrapper from "../maps/MapWrapper";
import { useApolloClient, useQuery } from "../util";
import createSilence from "/lib/mutation/createSilence";
import deleteSilence from "/lib/mutation/deleteSilence";
import deleteEvent from "/lib/mutation/deleteEvent";
import executeCheck from "/lib/mutation/executeCheck";
import resolveEvent from "/lib/mutation/resolveEvent";
import { ApolloError } from "apollo-client";
import { PollingDuration } from "/lib/constant";
import { isUnreachable } from "/lib/util/fetchError";

//extracted from EventsView.tsx from line 1203
//custom hooks to fetch required data for dashboard view
export const useDashboardViewData = () => {
  const client = useApolloClient();
  const onCreateSilence = React.useCallback(
    (vars) => createSilence(client, vars),
    [client],
  );
  const onDeleteSilence = React.useCallback(
    (vars) => deleteSilence(client, vars),
    [client],
  );
  const onResolve = React.useCallback((vars) => resolveEvent(client, vars), [
    client,
  ]);
  const onExecute = React.useCallback((vars) => executeCheck(client, vars), [
    client,
  ]);
  const onDelete = React.useCallback((vars) => deleteEvent(client, vars), [
    client,
  ]);

  const eventsDashViewQuery = gql`
    query EventsViewQuery(
      $namespace: String!
      $filters: [String!]
      $order: EventsListOrder
      $limit: Int
      $offset: Int
    ) {
      namespace(name: $namespace) {
        id
        name
        events(
          limit: $limit
          offset: $offset
          filters: $filters
          orderBy: $order
        ) @connection(key: "events", filter: ["filters", "orderBy"]) {
          pageInfo {
            totalCount
          }
        }
      }
    }
  `;

  const entitiesDashViewQuery = gql`
    query EntitiesViewQuery(
      $namespace: String!
      $limit: Int
      $offset: Int
      $order: EntityListOrder
      $filters: [String!]
    ) {
      namespace(name: $namespace) {
        id
        entities(
          limit: $limit
          offset: $offset
          filters: $filters
          orderBy: $order
        ) @connection(key: "entities", filter: ["filters", "orderBy"]) {
          pageInfo {
            totalCount
          }
        }
      }
    }
  `;

  const eventsViewFragments = {
    namespace: gql`
      fragment EventsView_namespace on Namespace {
        id
        ...EventsList_namespace
      }

      ${EventsList.fragments.namespace}
    `,
  };

  const eventsViewQuery = gql`
    query EventsViewQuery(
      $namespace: String!
      $filters: [String!]
      $order: EventsListOrder
      $limit: Int
      $offset: Int
    ) {
      namespace(name: $namespace) {
        id
        ...EventsView_namespace
      }
    }

    ${eventsViewFragments.namespace}
  `;

  const failingVariables = useEventsDashViewQueryVariables("Incident");
  const query = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: failingVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const failingChartVariables = useEventsDashViewQueryVariables("Unknown");
  const failingQuery = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: failingChartVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const passingVariables = useEventsDashViewQueryVariables("Passing");
  const passingQuery = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: passingVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const warningVariables = useEventsDashViewQueryVariables("Warning");
  const warningQuery = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: warningVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const criticalVariables = useEventsDashViewQueryVariables("Critical");
  const criticalQuery = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: criticalVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const eventsQuery = useQuery({
    query: eventsViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: failingVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const entitiesVariables = useEntitiesDashViewQueryVariables();
  const entitiesQuery = useQuery({
    query: entitiesDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: entitiesVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }
      throw error;
    },
  });

  const printerVariables = useEventsDashViewQueryVariables(
    "Incident",
    "printer",
  );
  const printerQuery = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: printerVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const callAttendantVariables = useEventsDashViewQueryVariables(
    "Incident",
    "call_attendant",
  );
  const callAttendantQuery = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: callAttendantVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  const bnaVariables = useEventsDashViewQueryVariables("Incident", "bna-check");
  const bnaQuery = useQuery({
    query: eventsDashViewQuery,
    fetchPolicy: "cache-and-network",
    pollInterval: PollingDuration.long,
    variables: bnaVariables,
    onError: (error: Error) => {
      if (isUnreachable((error as ApolloError).networkError)) {
        return;
      }

      throw error;
    },
  });

  return {
    onCreateSilence,
    onDeleteSilence,
    onResolve,
    onExecute,
    onDelete,
    failingVariables,
    query,
    failingChartVariables,
    failingQuery,
    passingVariables,
    passingQuery,
    warningVariables,
    warningQuery,
    criticalVariables,
    criticalQuery,
    eventsQuery,
    entitiesVariables,
    entitiesQuery,
    printerVariables,
    printerQuery,
    callAttendantVariables,
    callAttendantQuery,
    bnaVariables,
    bnaQuery,
  };
};

export const DashboardViewContent = () => {
  const portrait = window.matchMedia("(orientation: portrait)").matches;

  //call this data fetch hooks to get data
  const data = useDashboardViewData();

  //divider =====================================>
  if (portrait) {
    return (
      <AppLayout>
        <Grid container spacing={2}>
          <Grid xs={4} item>
            <EntitiesDashView type={"table"} />
          </Grid>
          <Grid xs={4} item>
            <EventsDashView type={"table"} data={data} />
          </Grid>
          <Grid xs={4} item>
            <EventsDashView type={"incidentChart"} data={data} />
          </Grid>
          <Grid xs={12} item>
            <EventsDashView type={"barChart"} data={data} />
          </Grid>
          <Grid xs={8} item>
            <EventsDashView type={"pieChart"} data={data} />
          </Grid>
          <Grid xs={4} item>
            <Grid container direction="column">
              <Grid item>
                <EventsDashView type={"callAttendantChart"} data={data} />
              </Grid>
              <Grid item>
                <EventsDashView type={"printerChart"} data={data} />
              </Grid>
              <Grid item>
                <EventsDashView type={"bnaChart"} data={data} />
              </Grid>
            </Grid>
          </Grid>
          <Grid xs={12} item>
            <MapWrapper />
          </Grid>
        </Grid>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Grid container spacing={2}>
        <Grid xs={4} item>
          <EntitiesDashView type={"table"} />
        </Grid>
        <Grid xs={4} item>
          <EventsDashView type={"table"} data={data} />
        </Grid>
        <Grid xs={4} item>
          <EventsDashView type={"incidentChart"} data={data} />
        </Grid>
        <Grid xs={8} item>
          <EventsDashView type={"barChart"} data={data} />
        </Grid>
        <Grid xs={4} item>
          <EventsDashView type={"pieChart"} data={data} />
        </Grid>
        <Grid xs={4} item>
          <Grid container direction="column">
            <Grid item>
              <EventsDashView type={"callAttendantChart"} data={data} />
            </Grid>
            <Grid item>
              <EventsDashView type={"printerChart"} data={data} />
            </Grid>
            <Grid item>
              <EventsDashView type={"bnaChart"} data={data} />
            </Grid>
          </Grid>
        </Grid>
        <Grid xs={8} item>
          <MapWrapper />
        </Grid>
      </Grid>
    </AppLayout>
  );
};

DashboardViewContent.defaultProps = {
  toolbarContent: <BoundFilterList />,
};

export const DashboardView = () => {
  return <DashboardViewContent />;
};
